<template>
  <div>
    <div
      v-if="
        selectedConsumptionDevicesOrder &&
        Object.keys(selectedConsumptionDevicesOrder).length !== 0
      "
      :key="selectedConsumptionDevicesOrder.id"
      class="consumption-devices-page-container"
    >
      <div class="page_title">{{ $t('consumption_devices.page_title') }}</div>

      <ConsumptionDevicesHeaderDetail
        :applicant-html="selectedConsumptionDevicesApplicant"
        :address-html="selectedConsumptionDevicesAdress"
        :status-config="orderStatusConfig"
        @go-back="goOnePageBack"
        @cancel-order="onClickCancelOrder"
        @start-inspection="onClickStartInspection"
      />
      <NewGlassCard
        v-if="
          selectedConsumptionDevicesOrder.status ===
          CONSUMPTION_DEVICES_STATUSES.in_inspection.value
        "
        title="Anmeldung prüfen"
        class="default-mb"
      >
        <ConsumptionDevicesInspectionComponent
          :selectedConsumptionDevicesOrder="selectedConsumptionDevicesOrder"
        />
      </NewGlassCard>

      <div :class="this.$vuetify.breakpoint.xl ? 'xl-design-details-page' : ''">
        <div>
          <!-- Order details -->
          <ConsumptionDevicesOrderDetails
            :consumptionDevicesOrder="selectedConsumptionDevicesOrder"
            @edit-component="editComponent($event)"
          />

          <!-- Technische Details -->
          <div v-if="!this.$vuetify.breakpoint.xl">
            <TechnicalDetails
              :consumptionDevicesOrder="selectedConsumptionDevicesOrder"
              @edit-component="editComponent($event)"
            />
          </div>

          <!-- Notices from Journey -->
          <NewGlassCard
            v-if="userQuestionsExist"
            :class="
              this.$vuetify.breakpoint.xl
                ? 'xl-design-details-page-element'
                : 'default-mb'
            "
            :title="$t('pv_order_questions.title')"
          >
            <OrderQuestions :orderQuestionsArray="userQuestionsArray" />
          </NewGlassCard>

          <!-- History Tables -->
          <div class="mt-12">
            <!-- Dokumentenhistorie -->
            <div>
              <NewGlassCard
                :class="
                  this.$vuetify.breakpoint.xl
                    ? 'xl-design-details-page-element default-mb'
                    : 'default-mb'
                "
                class="z-index-0"
                :title="$t('document_history.title')"
              >
                <DocumentHistoryComponent :historyType="'ConsumptionDevice'" />
              </NewGlassCard>
            </div>

            <!-- HistoryTable -->
            <div>
              <NewGlassCard
                :class="
                  this.$vuetify.breakpoint.xl
                    ? 'xl-design-details-page-element default-mb'
                    : 'default-mb'
                "
                class="z-index-0"
                :title="$t('history.title')"
              >
                <HistoryTable historyType="ConsumptionDevice" />
              </NewGlassCard>
            </div>
          </div>
        </div>

        <!-- Technische Details -->
        <div v-if="this.$vuetify.breakpoint.xl">
          <TechnicalDetails
            :consumptionDevicesOrder="selectedConsumptionDevicesOrder"
            @edit-component="editComponent($event)"
          />
        </div>
      </div>

      <NewModalDialog
        v-if="isModalVisible"
        :value="noteValue"
        role="dialog"
        :enable-note="enableNote"
        @update-note-data="updateNoteChangedObject"
        @cancel-data="closeModal"
      >
        <template v-slot:body>
          <DynamicComponent
            class="consumption-devices-form"
            :type="currentModalComponent"
            :componentProperties="currentModalComponentProps"
          />
          <v-alert
            type="error"
            class="mt-4"
            v-if="showModalUpdateError"
            text
            transition="scale-transition"
          >
            {{ $t('consumption_devices_details.saving_failed') }}
          </v-alert>
        </template>
        <template v-slot:footer>
          <div v-show="showSpinner" class="update-event-spinner">
            <v-progress-circular
              indeterminate
              color="primary"
              :size="60"
              :width="5"
            />
          </div>
          <div v-if="!showSpinner">
            <button type="button" class="btn-save" @click="submitComponent">
              {{ $t('consumption_devices_details.save') }}
            </button>
            <button type="button" class="btn-cancel" @click="closeModal">
              {{ $t('consumption_devices_details.cancel') }}
            </button>
          </div>
        </template>
      </NewModalDialog>
    </div>

    <div v-if="serverError">
      <h1>{{ $t('order_details.loading_error') }}</h1>
      <p>{{ $t('order_details.error_hint') }}</p>
    </div>
  </div>
</template>

<script>
import { initCDFiles } from '@/helpers/CDFiles';
import { mixinData } from '@/helpers/handleJSONPathKeys';
import { EventBus } from '@/main';
import {
  NewGlassCard,
  NewModalDialog,
} from '@newnetzportal/netzportal-webapp-storybook-components';
import gql from 'graphql-tag';
import ConsumptionDevicesHeaderDetail from '../components/consumption-devices-details/ConsumptionDevicesHeaderDetail.vue';
import ConsumptionDevicesInspectionComponent from '../components/consumption-devices-details/ConsumptionDevicesInspectionComponent.vue';
import ConsumptionDevicesOrderDetails from '../components/consumption-devices-details/ConsumptionDevicesOrderDetails.vue';
import DynamicComponent from '../components/consumption-devices-details/DynamicDialogBody';
import TechnicalDetails from '../components/consumption-devices-details/TechnicalDetails.vue';
import HistoryTable from '../components/history-table/HistoryTable';
import DocumentHistoryComponent from '../components/order-details/DocumentHistoryComponent';
import OrderQuestions from '../components/order-details/OrderQuestions';
import { translateConsumptionDevicesStatus } from '../helpers/ConsumptionDevicesStatusConfig';
import { CONSUMPTION_DEVICES_STATUSES } from '../helpers/constants';
import {
  installationFullAddressObject,
  personalDataObject,
} from '../helpers/transformToPresentableData';
import {
  getConsumptionDevicesOrderById,
  updateConsumptionDevicesOrderStatus,
} from '../services/ConsumptionDevicesService';

export default {
  name: 'ConsumptionDevicesDetailsPage',
  components: {
    ConsumptionDevicesHeaderDetail,
    ConsumptionDevicesOrderDetails,
    TechnicalDetails,
    NewModalDialog,
    DynamicComponent,
    OrderQuestions,
    NewGlassCard,
    HistoryTable,
    DocumentHistoryComponent,
    ConsumptionDevicesInspectionComponent,
  },
  data() {
    return {
      consumptionDevicesOrderID: this.$route.params.id
        ? this.$route.params.id
        : '',
      serverError: false,
      isModalVisible: false,
      currentModalComponent: null,
      currentModalComponentProps: {},
      enableNote: false,
      noteValue: {
        note: '',
      },
      CONSUMPTION_DEVICES_STATUSES: CONSUMPTION_DEVICES_STATUSES,
    };
  },
  async created() {
    // for Firefox
    window.addEventListener('scroll', () => {
      document.documentElement.style.setProperty(
        '--scroll-y',
        `${window.scrollY}px`
      );
    });
    if (this.consumptionDevicesOrderID) {
      await this.getConsumptionDevicesOrderFromBackend(
        this.consumptionDevicesOrderID
      );
    }
  },
  computed: {
    showSpinner: {
      get() {
        return this.$store.getters.getShowSpinnerInModal;
      },
      set(value) {
        this.$store.commit('setShowSpinnerInModal', value);
      },
    },
    showModalUpdateError: {
      get() {
        return this.$store.getters.getShowModalUpdateError;
      },
      set(value) {
        this.$store.commit('setShowModalUpdateError', value);
      },
    },
    selectedConsumptionDevicesOrder() {
      return this.$store.getters.getSelectedConsumptionDevice;
    },
    selectedConsumptionDevicesApplicant() {
      const order = this.selectedConsumptionDevicesOrder;
      const customerData = order.end_customer || order.applicant;
      return personalDataObject(customerData);
    },
    selectedConsumptionDevicesAdress() {
      return installationFullAddressObject(
        this.selectedConsumptionDevicesOrder.address
      );
    },
    orderStatusConfig() {
      return translateConsumptionDevicesStatus(
        this.selectedConsumptionDevicesOrder.status
      );
    },
    userQuestionsExist() {
      return !!this.selectedConsumptionDevicesOrder.user_questions?.length;
    },
    userQuestionsArray() {
      return [
        {
          title: this.$t('consumption_devices.page_title'),
          questions: this.selectedConsumptionDevicesOrder.user_questions,
        },
      ];
    },
  },
  methods: {
    async fetchAndInitFiles() {
      await initCDFiles(this.selectedConsumptionDevicesOrder);
    },
    submitComponent() {
      this.showModalUpdateError = false;
      this.showSpinner = true;
      new Promise((resolve, reject) => {
        EventBus.$emit(`submit-${this.currentModalComponent}`, {
          resolve,
          reject,
        });
      })
        .then(() => {
          this.closeModal();
        })
        .catch(() => {
          this.showModalUpdateError = true;
          this.showSpinner = false;
        });
    },
    getConsumptionDevicesOrderFromBackend(Id) {
      getConsumptionDevicesOrderById(Id)
        .then((res) => {
          if (!res) {
            console.warn(
              'No data received for the selected consumption device.'
            );
            this.serverError = true;
            return;
          }
          this.$store.commit('setSelectedConsumptionDevicesOrder', res);
          this.fetchAndInitFiles();
        })
        .catch((err) => {
          console.error('Error fetching consumption device by ID:', err);
          this.serverError = true;
        });
    },
    goOnePageBack() {
      const previousPage = this.$store.getters.getPreviousPage;
      if (previousPage) {
        this.$router.push(previousPage);
      } else {
        this.$router.push('/');
      }
    },
    onClickCancelOrder() {
      this.cancelOrder();
    },
    async cancelOrder() {
      try {
        this.updateConsumptionDevicesOrderStatus(
          CONSUMPTION_DEVICES_STATUSES.cancelled.value
        );
      } catch (err) {
        console.log(err);
      }
    },
    onClickStartInspection() {
      this.startInspection();
    },
    startInspection() {
      this.updateConsumptionDevicesOrderStatus(
        CONSUMPTION_DEVICES_STATUSES.in_inspection.value
      );
    },
    updateConsumptionDevicesOrderStatus(status) {
      updateConsumptionDevicesOrderStatus(
        {
          id: this.selectedConsumptionDevicesOrder.id,
          old_value: {
            status: this.selectedConsumptionDevicesOrder.status,
          },
          new_value: {
            status: status,
          },
        },
        this.$store.getters.getUserId
      )
        .then((res) => {
          if (res) {
            const new_status = { status: status };
            this.$store.commit(
              'updateSelectedConsumptionDevicesData',
              new_status
            );
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    editComponent(data) {
      this.currentModalComponent = data.component;
      this.currentModalComponentProps = data.properties ? data.properties : {};
      this.enableNote = data.enableNote;
      document.body.classList.add('modal-open');
      this.disableScrolling();
      this.noteValue.note = '';
      this.isModalVisible = true;
      this.showModalUpdateError = false;
    },
    closeModal() {
      this.isModalVisible = false;
      document.body.classList.remove('modal-open');
      this.enableScrolling();
      this.showSpinner = false;
      this.showModalUpdateError = false;
    },
    updateNoteChangedObject(note) {
      this.noteValue.note = note;
      this.$store.commit('updateNoteChangedObject', note);
    },
    disableScrolling() {
      const scrollY =
        document.documentElement.style.getPropertyValue('--scroll-y');
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}`;
      body.style.width = '100%';
    },
    enableScrolling() {
      const body = document.body;
      const scrollY = body.style.top;
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    },
  },
  apollo: {
    $subscribe: {
      tags: {
        query: gql`
          subscription onConsumptionDeviceHistoryChanged($deviceId: String) {
            onConsumptionDeviceHistoryChanged(id: $deviceId) {
              id
              changed_by
              event_origin
              created
              field
              title
              old_value
              new_value
              note
              status
              filename
            }
          }
        `,
        skip() {
          return !this.consumptionDevicesOrderID;
        },
        error(error) {
          console.error(
            '[GraphQL] ConsumptionDevices - Subscription error:',
            error
          );
          console.error(
            '[GraphQL] ConsumptionDevices - Subscription error details:',
            JSON.stringify(error, null, 2)
          );
        },
        variables() {
          return {
            deviceId: this.consumptionDevicesOrderID,
          };
        },
        result({ data }) {
          const eventData = data.onConsumptionDeviceHistoryChanged;
          let oldValueParsed = null;
          let newValueParsed = null;

          try {
            oldValueParsed = eventData.old_value
              ? JSON.parse(eventData.old_value)
              : null;
            newValueParsed = eventData.new_value
              ? JSON.parse(eventData.new_value)
              : null;
          } catch (e) {
            console.error(
              'Error parsing history values for Consumption Device:',
              e
            );
            return;
          }

          // Check for JSONPath keys BEFORE calling mixinData
          const isJSONPathKey = Object.keys(newValueParsed ?? {}).some((key) =>
            key.startsWith('$')
          );

          oldValueParsed = mixinData(oldValueParsed);
          newValueParsed = mixinData(newValueParsed);

          // Construct payload - fix template variables
          const payload = {
            id: eventData.id,
            new_value: newValueParsed,
            old_value: oldValueParsed,
            title:
              eventData.title === '$context.prev.result.payload.title'
                ? null
                : eventData.title,
            note:
              eventData.note === '$context.prev.result.payload.note'
                ? null
                : eventData.note,
            filename: eventData.filename,
            status:
              eventData.status && typeof eventData.status !== 'string'
                ? JSON.parse(eventData.status)
                : eventData.status || null,
          };
          eventData.payload = JSON.stringify(payload);

          // Conditional store update
          if (isJSONPathKey) {
            this.$store.commit(
              'updateExactParamsInSelectedConsumptionDevice',
              newValueParsed
            );
          } else if (newValueParsed) {
            this.$store.commit(
              'updateSelectedConsumptionDevicesData',
              newValueParsed
            );
          }

          // Update metadata
          this.$store.commit(
            'updateLastChangedByInSelectedConsumptionDevice',
            eventData.changed_by
          );
          this.$store.commit(
            'updateLastUpdateInSelectedConsumptionDevice',
            eventData.created
          );
          this.$store.commit('setNotifyEvent', eventData);

          EventBus.$emit('update-history-table-consumptiondevice', eventData);
        },
      },
    },
  },
};
</script>

<style scoped lang="scss">
.page_title {
  font-size: 60px;
}
.card-without-title > .frosted_glass_card_title {
  margin-bottom: 0px !important;
}
</style>

<style>
.consumption-devices-page-container #action {
  background-color: rgba(255, 255, 255, 0.9);
  margin-left: 1px;
}
</style>
